// Компактный markdown-рендерер для чата.
// Поддерживает: #..###### headings, **bold**, *italic*, `code`, ```code```,
// > blockquote, --- hr, * / 1. lists, GitHub-flavored tables, ссылки [text](url),
// inline-line-break (одинарный \n внутри параграфа).
// Всё с HTML-escape, без зависимостей.

function esc(s: string): string {
  return s.replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!
  ));
}

function inline(s: string): string {
  // code first (защищает остальное от обработки)
  const codes: string[] = [];
  s = s.replace(/`([^`\n]+)`/g, (_, c) => {
    codes.push(`<code>${esc(c)}</code>`);
    return `\x00${codes.length - 1}\x00`;
  });
  s = esc(s);
  // bold + italic
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  // links [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_, t, u) => `<a href="${u.replace(/"/g, "&quot;")}" target="_blank" rel="noopener">${t}</a>`);
  // restore inline-code
  s = s.replace(/\x00(\d+)\x00/g, (_, i) => codes[+i]);
  return s;
}

export function renderMarkdown(src: string): string {
  if (!src) return "";
  const out: string[] = [];
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      const lang = fence[1] || "";
      i++;
      const buf: string[] = [];
      while (i < lines.length && !/^```\s*$/.test(lines[i])) buf.push(lines[i++]);
      i++; // closing fence
      out.push(`<pre><code class="lang-${esc(lang)}">${esc(buf.join("\n"))}</code></pre>`);
      continue;
    }

    // heading
    const hd = line.match(/^(#{1,6})\s+(.*)$/);
    if (hd) {
      out.push(`<h${hd[1].length}>${inline(hd[2].trim())}</h${hd[1].length}>`);
      i++;
      continue;
    }

    // hr
    if (/^(\s*[-*_]\s*){3,}$/.test(line) && line.trim().length >= 3) {
      out.push("<hr>");
      i++;
      continue;
    }

    // table — line is "| ... |" and next is "|---|---|"
    if (/^\s*\|.+\|\s*$/.test(line) && i + 1 < lines.length
        && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(lines[i + 1])) {
      const header = line.trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim());
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.+\|\s*$/.test(lines[i])) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim()));
        i++;
      }
      const thead = `<thead><tr>${header.map(h => `<th>${inline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows.map(r =>
        `<tr>${r.map(c => `<td>${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody>`;
      out.push(`<div class="md-table-wrap"><table class="md-table">${thead}${tbody}</table></div>`);
      continue;
    }

    // blockquote
    if (/^\s*>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push(`<blockquote>${renderMarkdown(buf.join("\n"))}</blockquote>`);
      continue;
    }

    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ""));
        i++;
      }
      out.push(`<ul>${items.map(it => `<li>${inline(it)}</li>`).join("")}</ul>`);
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push(`<ol>${items.map(it => `<li>${inline(it)}</li>`).join("")}</ol>`);
      continue;
    }

    // blank
    if (/^\s*$/.test(line)) { i++; continue; }

    // paragraph — collect until blank/heading/list/etc
    const buf: string[] = [line];
    i++;
    while (i < lines.length
           && !/^\s*$/.test(lines[i])
           && !/^#{1,6}\s+/.test(lines[i])
           && !/^```/.test(lines[i])
           && !/^\s*[-*+]\s+/.test(lines[i])
           && !/^\s*\d+\.\s+/.test(lines[i])
           && !/^\s*>\s?/.test(lines[i])
           && !/^\s*\|.+\|\s*$/.test(lines[i])) {
      buf.push(lines[i++]);
    }
    out.push(`<p>${buf.map(inline).join("<br>")}</p>`);
  }

  return out.join("");
}
